// src/context/NotificationContext.jsx
// In-app replacement for browser alert() / window.confirm().
// Usage:
//   const { notify, confirmDialog } = useNotification();
//   notify('Saved successfully', 'success');
//   const ok = await confirmDialog('Delete this employee?');

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { CheckCircle, AlertCircle, Info, X, AlertTriangle } from 'lucide-react';

const NotificationContext = createContext(null);

const TOAST_ICONS = {
  success: <CheckCircle className="w-5 h-5 text-green-600" />,
  error: <AlertCircle className="w-5 h-5 text-red-600" />,
  info: <Info className="w-5 h-5 text-blue-600" />,
};

const TOAST_STYLES = {
  success: 'bg-green-50 border-green-100 text-green-800',
  error: 'bg-red-50 border-red-100 text-red-800',
  info: 'bg-blue-50 border-blue-100 text-blue-800',
};

export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const idRef = useRef(0);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((message, type = 'info', durationMs = 4000) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    if (durationMs) {
      setTimeout(() => dismissToast(id), durationMs);
    }
  }, [dismissToast]);

  const confirmDialog = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setConfirmState({
        message,
        title: options.title || 'Please confirm',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        danger: options.danger !== false,
        resolve,
      });
    });
  }, []);

  const handleConfirmResult = (result) => {
    if (confirmState) confirmState.resolve(result);
    setConfirmState(null);
  };

  return (
    <NotificationContext.Provider value={{ notify, confirmDialog }}>
      {children}

      {/* Toast stack */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 w-full max-w-sm">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={'flex items-start gap-2.5 border rounded-control shadow-elevated px-4 py-3 text-sm ' + TOAST_STYLES[t.type]}
          >
            {TOAST_ICONS[t.type]}
            <span className="flex-1 whitespace-pre-line">{t.message}</span>
            <button onClick={() => dismissToast(t.id)} className="opacity-60 hover:opacity-100 shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Confirm modal */}
      {confirmState && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[110]">
          <div className="bg-white rounded-card shadow-elevated w-full max-w-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-2.5">
              <AlertTriangle className={'w-5 h-5 ' + (confirmState.danger ? 'text-red-500' : 'text-primary')} />
              <h3 className="font-bold text-gray-900 text-sm">{confirmState.title}</h3>
            </div>
            <div className="px-6 py-4 text-sm text-gray-600 whitespace-pre-line">
              {confirmState.message}
            </div>
            <div className="px-6 py-4 border-t border-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => handleConfirmResult(false)}
                className="px-4 py-2 rounded-control border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                {confirmState.cancelLabel}
              </button>
              <button
                onClick={() => handleConfirmResult(true)}
                className={'px-4 py-2 rounded-control text-sm font-medium text-white ' + (confirmState.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primary-dark')}
              >
                {confirmState.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotification must be used within NotificationProvider');
  return ctx;
};